/**
 * Greys the Klara screens need and the app's palette does not declare.
 *
 * The code reached for --color-black40, --color-black05 and --color-black10,
 * none of which exist in globalVariables.less. An undefined custom property
 * makes the whole declaration invalid at computed-value time, so every caption
 * fell back to the inherited body black and every hairline resolved to
 * `none 0px` — i.e. did not render at all.
 *
 * MUTED_TEXT is the caption grey from the Figma file. The two rule greys keep
 * the weights the original names implied, and sit next to the `rgba(0,0,0,.12)`
 * that theme.ts already uses for input underlines and table headers.
 *
 * Replace these with real tokens once the palette grows a black scale.
 */
export const MUTED_TEXT = '#949494';

/**
 * The size line on a source file row. A blue-grey, distinct from MUTED_TEXT in
 * the design rather than by accident — see the `load_file` component.
 */
export const FILE_META = '#94a3b8';
export const CARD_BORDER = 'rgba(0, 0, 0, 0.12)';
export const ROW_SEPARATOR = 'rgba(0, 0, 0, 0.06)';

/**
 * The ground under a section card on Zadání kurzu — a shade off white, so the
 * cards read as cards against the white tab panel. `--color-background` is the
 * app's own near-white and is two shades darker than the mock's #fafafa; this
 * is the lighter one the design uses for panels sitting *on* white.
 */
export const SECTION_BACK = '#fafafa';

/**
 * The ground under a field Klara drafted. `--color-primary15` is the palette's
 * own name for this tint — primary at 15 %, which resolves to the #EAF4D8 the
 * design names Tertiary/100 — so a re-coloured space tints its drafts in its
 * own colour rather than in Knowspread green.
 */
export const DRAFT_GROUND = 'var(--color-primary15)';

/**
 * The provenance line under such a field. Lighter than MUTED_TEXT on purpose:
 * the design sets these two greys apart, the caption being the quieter of the
 * two.
 */
export const DERIVED_HINT = '#b8b8b8';

/**
 * The rule down the left edge of a process-log row.
 *
 * The mock draws it teal (#008c9b), the same family as the info banner beside
 * it — not the green the review read into it, and not a colour the palette
 * declares. It is the same teal that open decision 4.6 asks about on the
 * Výstupy download buttons, so it lives here as one constant to change once
 * that is settled.
 */
export const LOG_RULE = '#008c9b';

/**
 * The ground under the right half of Vstupy — Klara's half. The design paints
 * it for the full height of the panel, and that ground is the only thing that
 * tells the two halves apart at a glance.
 */
export const PANEL_BACK = '#f9fafb';

/**
 * The teal the design writes its notes in — the same family as the info banner
 * on the waiting screens, and the same open question as `LOG_RULE`.
 */
export const NOTE_TEXT = '#006d7a';

/** The drop zone: a dashed slate frame on near-white. */
export const DROPZONE_BACK = '#f8fafc';
export const DROPZONE_BORDER = '#cbd5e1';
export const DROPZONE_TEXT = '#334155';
export const DROPZONE_HINT = '#94a3b8';

/** The meta line on an output card — a mid grey the palette does not declare. */
export const CARD_META = '#6b7280';

/** The frame and the empty thumbnail ground of a gallery tile. */
export const GALLERY_BORDER = '#e5e7eb';
